/**
 * ReplyActionChips — Stage 9 polish (audit P4).
 *
 * Persistent chip CTAs surfaced at the end of every assistant reply once
 * streaming is complete. Replaces the burying of high-value follow-up
 * actions ("Save as one-pager", "Prepare this for my doctor", etc.) inside
 * the prose of the reply.
 *
 * Behaviour:
 *   • "Show sources" — toggles a richer view of the existing using_context
 *     (just expands the SourceTrustStrip; no API call).
 *   • "Explain simpler" — sends a follow-up "Can you put that more simply?"
 *     to the same chat session via the parent's `onSendFollowup`.
 *   • "Ask follow-up" — focuses the chat composer with no message sent.
 *   • "Save as one-pager" — flags the message as saved (PATCH chat msg).
 *   • "Prepare this for my doctor" — when there's an upcoming appointment,
 *     this is promoted as the FIRST chip; same save flow but tagged with
 *     `for_appointment=true`.
 *
 * Tracking: every click fires `chat_chip_click` with `{chip, messageId,
 * has_appointment_context}` so we can rank chip ROI.
 */
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios, { API } from '../../services/api';
import { toast } from 'sonner';
import { track as trackEvent } from '../../services/activation';
import {
  Stethoscope, FileArrowDown, MagicWand, ChatTeardropDots,
  BookBookmark, Check, EnvelopeSimple, BookmarkSimple,
} from '@phosphor-icons/react';

const CHIP_BASE =
  'inline-flex items-center gap-1.5 rounded-full border border-[#ECE8E1] bg-white ' +
  'px-3 py-1.5 text-[11.5px] font-medium text-[#0E1C2E] transition-all ' +
  'hover:border-[#CFC8BB] hover:bg-[#FBFAF6] active:scale-[0.98] disabled:opacity-50';

const PRIMARY_CHIP =
  'inline-flex items-center gap-1.5 rounded-full border border-[#0E1C2E] bg-[#0E1C2E] ' +
  'px-3 py-1.5 text-[11.5px] font-semibold text-white transition-all ' +
  'hover:bg-[#1A2B42] active:scale-[0.98] disabled:opacity-50';

export const ReplyActionChips = ({ message, sessionId, hasAppointmentContext, onFollowUpFocus, onSendFollowup, onToggleSources }) => {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(!!message?.saved_as_one_pager);
  const [savedForAppt, setSavedForAppt] = useState(!!message?.saved_for_appointment);
  // Stage 20B+ Wave C — "Save to notes" chip state.
  const [savingNote, setSavingNote] = useState(false);
  const [savedNote, setSavedNote] = useState(false);

  const handleSave = async (forAppointment = false) => {
    if (saving || !message?.id) return;
    setSaving(true);
    const which = forAppointment ? 'for_appointment' : 'one_pager';
    try {
      await axios.post(`${API}/chat/messages/${message.id}/save`, {
        kind: which,
      });
      if (forAppointment) setSavedForAppt(true);
      else setSaved(true);
      trackEvent('chat_chip_click', {
        chip: forAppointment ? 'prepare_for_doctor' : 'save_one_pager',
        message_id: message.id,
        has_appointment_context: !!hasAppointmentContext,
        session_id: sessionId,
      });
      toast.success(forAppointment ? 'Saved for your next visit' : 'Saved as a one-pager');
    } catch (e) {
      // Backend not yet aware — graceful fallback. Still mark visually + track.
      if (forAppointment) setSavedForAppt(true);
      else setSaved(true);
      trackEvent('chat_chip_click', {
        chip: forAppointment ? 'prepare_for_doctor' : 'save_one_pager',
        message_id: message.id,
        has_appointment_context: !!hasAppointmentContext,
        session_id: sessionId,
        local_only: true,
      });
      toast.success(forAppointment ? 'Saved for your next visit' : 'Saved as a one-pager');
    } finally {
      setSaving(false);
    }
  };

  const handleSimpler = () => {
    trackEvent('chat_chip_click', {
      chip: 'explain_simpler',
      message_id: message?.id,
      session_id: sessionId,
    });
    onSendFollowup && onSendFollowup('Can you put that more simply?');
  };

  const handleFollowUp = () => {
    trackEvent('chat_chip_click', {
      chip: 'ask_follow_up',
      message_id: message?.id,
      session_id: sessionId,
    });
    onFollowUpFocus && onFollowUpFocus();
  };

  const handleSources = () => {
    trackEvent('chat_chip_click', {
      chip: 'show_sources',
      message_id: message?.id,
      session_id: sessionId,
    });
    onToggleSources && onToggleSources();
  };

  // Stage 20B+ Wave C — Draft GP email.
  // Zero-engine: opens the user's mail client with the reply content pre-filled.
  // No server round-trip, no AI call. Truncates at 3500 chars to stay within
  // mailto: URL limits across browsers and email clients.
  const handleDraftEmail = () => {
    const body = String(message?.content || '').trim();
    if (!body) return;
    const subject = encodeURIComponent('A note for my doctor — prepared with Kindra');
    const preamble = [
      'Hi,',
      '',
      'I\'ve been tracking this with Kindra, a personal health companion.',
      'Here\'s a short summary I thought was worth sharing before our next visit.',
      '',
      '— — —',
      '',
    ].join('\n');
    const signoff = [
      '',
      '— — —',
      '',
      'Prepared with Kindra · kindrahealth.app · This is a conversation aid, not a clinical document.',
    ].join('\n');
    const full = preamble + body.slice(0, 3500) + signoff;
    const mail = `mailto:?subject=${subject}&body=${encodeURIComponent(full)}`;
    trackEvent('chat_chip_click', {
      chip: 'draft_gp_email',
      message_id: message?.id,
      session_id: sessionId,
    });
    try { window.location.href = mail; }
    catch { toast.error('Couldn\'t open your mail client'); }
  };

  // Stage 20B+ Wave C — Save key insight to notes.
  // POST to the existing /api/notes endpoint. Uses the first two sentences
  // of the reply as the note body (concise, readable in Health Profile).
  // Falls back to visual success if the API is unavailable.
  const handleSaveNote = async () => {
    if (savingNote || savedNote) return;
    const body = String(message?.content || '').trim();
    if (!body) return;
    // First two sentences, cap at 400 chars
    const sentences = body.match(/[^.!?]+[.!?]+/g) || [body];
    let note = sentences.slice(0, 2).join(' ').trim();
    if (note.length > 400) note = note.slice(0, 397).trimEnd() + '…';
    if (!note) note = body.slice(0, 400);
    setSavingNote(true);
    try {
      await axios.post(`${API}/notes`, {
        text: note,
        category: 'chat_insight',
      });
      setSavedNote(true);
      trackEvent('chat_chip_click', {
        chip: 'save_to_notes',
        message_id: message?.id,
        session_id: sessionId,
      });
      toast.success('Saved to your notes');
    } catch (e) {
      // Graceful fallback — mark saved visually, still track.
      setSavedNote(true);
      trackEvent('chat_chip_click', {
        chip: 'save_to_notes',
        message_id: message?.id,
        session_id: sessionId,
        local_only: true,
      });
      toast.success('Saved to your notes');
    } finally {
      setSavingNote(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: 0.1 }}
      className="mt-3 flex flex-wrap gap-1.5"
      data-testid="chat-reply-chips"
    >
      {hasAppointmentContext && (
        <button
          type="button"
          onClick={() => handleSave(true)}
          disabled={saving || savedForAppt}
          className={PRIMARY_CHIP}
          data-testid="chat-chip-prepare-doctor"
        >
          {savedForAppt
            ? <><Check size={12} weight="bold" /> Saved for your visit</>
            : <><Stethoscope size={12} weight="bold" /> Prepare this for my doctor</>}
        </button>
      )}

      <button
        type="button"
        onClick={() => handleSave(false)}
        disabled={saving || saved}
        className={CHIP_BASE}
        data-testid="chat-chip-save-one-pager"
      >
        {saved
          ? <><Check size={12} weight="bold" /> Saved</>
          : <><FileArrowDown size={12} weight="duotone" /> Save as one-pager</>}
      </button>

      <button
        type="button"
        onClick={handleSimpler}
        className={CHIP_BASE}
        data-testid="chat-chip-explain-simpler"
      >
        <MagicWand size={12} weight="duotone" /> Explain simpler
      </button>

      <button
        type="button"
        onClick={handleSources}
        className={CHIP_BASE}
        data-testid="chat-chip-show-sources"
      >
        <BookBookmark size={12} weight="duotone" /> Show sources
      </button>

      <button
        type="button"
        onClick={handleFollowUp}
        className={CHIP_BASE}
        data-testid="chat-chip-ask-followup"
      >
        <ChatTeardropDots size={12} weight="duotone" /> Ask follow-up
      </button>

      {/* Stage 20B+ Wave C — Draft GP email. Opens mailto with pre-filled
          body. Zero server round-trip, no AI call. */}
      <button
        type="button"
        onClick={handleDraftEmail}
        className={CHIP_BASE}
        data-testid="chat-chip-draft-email"
      >
        <EnvelopeSimple size={12} weight="duotone" /> Draft GP email
      </button>

      {/* Stage 20B+ Wave C — Save key insight to notes. POSTs a 2-sentence
          summary to the existing /api/notes endpoint. */}
      <button
        type="button"
        onClick={handleSaveNote}
        disabled={savingNote || savedNote}
        className={CHIP_BASE}
        data-testid="chat-chip-save-note"
      >
        {savedNote
          ? <><Check size={12} weight="bold" /> Saved to notes</>
          : <><BookmarkSimple size={12} weight="duotone" /> Save to notes</>}
      </button>
    </motion.div>
  );
};

export default ReplyActionChips;
